import type { LoadBookActionType } from './player.actions'
import {
    CLOSE,
    LOAD_BOOK,
    MAXIMIZE,
    MINIMIZE,
    OPEN,
    PLAY,
    PAUSE,
    SELECT_CHAPTER,
    UPDATE_PROGRESS_BAR,
    UPDATE_PROGRESS_BAR_MANUAL,
    UPDATE_CURRENT_TRACK_TIME,
    UPDATE_VOLUME,
    UPDATE_RATE,
    OPEN_CHAPTER_LIST,
    CLOSE_CHAPTER_LIST,
    FORWARD_TRACK,
    BACK_TRACK,
    UPDATE_CURRENT_CHAPTER_PROGRESS,
    UPDATE_CHAPTER_HAS_LISTENED,
    LOADING,
    SET_PREVIEW,
    SET_PREVIEW_LIMIT_REACHED
} from './player.actions'
import type { PlayerState } from './player.types'
import { tokens } from '@arvoreeducacao/bonsai-ds'

export const INITIAL_STATE: PlayerState = {
  isOpen: false,
  isMinimized: false,
  isPlaying: false,
  showChaptersList: false,
  progressBar: 0,
  isDragProgressBar: false,
  currentTrackTime: 0,
  targetTrackPecentage: 0,
  backTrack: false,
  forwardTrack: false,
  volume: 100,
  rate: 1,
  name: '',
  slug: '',
  author: '',
  imageUrlThumb: '',
  loading: false,
  isPreview: false,
  hasReachedPreviewLimit: false,
  currentChapter: { id: 1, stopedAt: 0 },
  audioBooks: []
}

const loadBookHandler = (
  state: PlayerState,
  action: LoadBookActionType
): PlayerState => {
  const currentTrack = action.book.audioBooks.find(
    audioBook => audioBook.active === true
  )

  const sameBook = action.book.slug === state.slug

  const currentChapter = sameBook
    ? state.currentChapter
    : {
        id: currentTrack.id,
        stopedAt: currentTrack.listenedTimestamp
      }
  const audioBooks = sameBook ? state.audioBooks : action.book.audioBooks

  return {
    ...state,
    name: action.book.name,
    slug: action.book.slug,
    author: action.book.author,
    imageUrlThumb: action.book.imageUrlThumb,
    audioBooks,
    currentChapter,
    hasReachedPreviewLimit: sameBook ? state.hasReachedPreviewLimit : false
  }
}

const reducer = (state: PlayerState = INITIAL_STATE, action): PlayerState => {
  const isDesktop = window.innerWidth > tokens.sizes.mdScreen

  switch (action.type) {
    case OPEN:
      return {
        ...state,
        isOpen: true,
        isMinimized: false,
        showChaptersList: isDesktop
      }

    case CLOSE:
      return {
        ...state,
        isOpen: false
      }

    case MINIMIZE:
      return {
        ...state,
        isMinimized: true,
        showChaptersList: false
      }

    case MAXIMIZE:
      return {
        ...state,
        isOpen: true,
        isMinimized: false,
        showChaptersList: isDesktop
      }

    case PLAY:
      return {
        ...state,
        isPlaying: true
      }

    case PAUSE:
      return {
        ...state,
        isPlaying: false
      }

    case UPDATE_PROGRESS_BAR:
      return {
        ...state,
        isDragProgressBar: action.payload.isDrag,
        progressBar: action.payload.value
      }

    case UPDATE_PROGRESS_BAR_MANUAL:
      return {
        ...state,
        isDragProgressBar: false,
        targetTrackPecentage: action.payload
      }

    case UPDATE_CURRENT_TRACK_TIME:
      return {
        ...state,
        currentTrackTime: action.payload
      }

    case UPDATE_CURRENT_CHAPTER_PROGRESS:
      return {
        ...state,
        currentChapter: {
          ...state.currentChapter,
          stopedAt: action.payload
        }
      }

    case UPDATE_VOLUME:
      return {
        ...state,
        volume: action.payload
      }

    case UPDATE_RATE:
      return {
        ...state,
        rate: action.payload
      }

    case OPEN_CHAPTER_LIST:
      return {
        ...state,
        showChaptersList: true
      }

    case CLOSE_CHAPTER_LIST:
      return {
        ...state,
        showChaptersList: false
      }

    case FORWARD_TRACK:
      return {
        ...state,
        forwardTrack: action.payload
      }

    case BACK_TRACK:
      return {
        ...state,
        backTrack: action.payload
      }

    case LOADING:
      return {
        ...state,
        loading: action.payload
      }

    case SET_PREVIEW:
      return {
        ...state,
        isPreview: action.payload
      }

    case SET_PREVIEW_LIMIT_REACHED:
      return {
        ...state,
        hasReachedPreviewLimit: action.payload,
        isPlaying: action.payload ? false : state.isPlaying
      }

    case SELECT_CHAPTER:
      return {
        ...state,
        audioBooks: state.audioBooks.map(audioBook => {
          return {
            ...audioBook,
            active: audioBook.id === action.id
          }
        }),
        currentChapter: {
          id: action.id,
          stopedAt: 0
        }
      }

    case LOAD_BOOK:
      return loadBookHandler(state, action)

    case UPDATE_CHAPTER_HAS_LISTENED:
      return {
        ...state,
        audioBooks: state.audioBooks.map(audiobook => {
          if (audiobook.id === action.payload.id) {
            return {
              ...audiobook,
              hasListened: true
            }
          }
          return audiobook
        })
      }
    default:
      return state
  }
}

export default reducer
